import { Box, CardContent, CardHeader, Grid } from "@mui/material";
import moment from 'moment';
import { useHolidays } from "../Hooks/useHolidays";
import Day from "./Day";
import { JSX, useState } from "react";
import { LeaveDay, useFerieApi } from "../Hooks/useFerieApi";
import config from '../Config/environments';
import { useIsAuthenticated } from "@azure/msal-react";
interface MonthProps {
    month: number;
    year: number;
    vacation?: LeaveDay[];
    onVacationUpdate?: (updatedVacation: LeaveDay[]) => void;
    activityType?: number;
}

const Month: React.FC<MonthProps> = (props) => {
    const isAuthenticated = useIsAuthenticated();
    
    var holidays = useHolidays(props.year);
    var api = useFerieApi();
    const [isUpdatingWeek, setIsUpdatingWeek] = useState(false);
    const [updatingWeekNumber, setUpdatingWeekNumber] = useState<number | null>(null);

    const getMonthName = (month: number): string => {
        return moment().month(month - 1).format('MMMM');
    };
    const getDaysBeforeFirst = (): number => {
        var v = new Date(props.year, props.month - 1, 1);
        return (v.getDay() + 6) % 7;
    }

    const getDaysOfMonth = (): number => {
        var v = new Date(props.year, props.month, 0);
        return v.getDate();
    }
    const getNumberOfWeeks = (): number => {
        var d = getDaysBeforeFirst();
        var p = getDaysOfMonth();
        if (d + p === 28) return 4;
        if (d + p <= 35) return 5;
        return 6;
    }
    const weekIsInPast = (weekIndex: number, year: number): boolean => {
        // return true if today is more than 5 days into the week or if the week is in the past
        const today = moment();
        const weekStart = moment().year(year).isoWeek(weekIndex).startOf('isoWeek');
        return weekStart.isBefore(today, 'week') || (today.isSame(weekStart, 'week') && today.day() > 5);
    }
    const handleWeekNumberClick = async (year: number, weekIndex: number) => {
        if (!isAuthenticated) return;
        if (isUpdatingWeek || weekIsInPast(weekIndex, year))
            return; // Prevent multiple concurrent week updates

        setIsUpdatingWeek(true);
        setUpdatingWeekNumber(weekIndex);

        const firstDay = moment().year(year).isoWeek(weekIndex).startOf('isoWeek');
        const toggleDates: string[] = [];
        var toggleOn = true;
        for (let i = 0; i < 5; i++) {
            const date = firstDay.clone().add(i, 'days');
            const vac = props.vacation?.findIndex(v => {
                const [vYear, vMonth, vDay] = v.date.split('-').map(Number);
                return vYear === date.year() && vMonth === date.month() + 1 && vDay === date.date();
            });
            if (i === 0 && vac !== undefined && vac >= 0) {
                toggleOn = false;
            }
            if (holidays.isHoliday(date.toDate())) {
                continue;
            }
            if (toggleOn && vac && vac >= 0) {
                continue;
            }
            if (!toggleOn && (vac === undefined || vac < 0)) {
                continue;
            }
            toggleDates.push(date.format('YYYY-MM-DD'));
        }
        const vacation = await api.toggleDays(toggleDates, toggleOn ? (api.ToggleMode.On) : (api.ToggleMode.Off), year, props.activityType || config.holidayCode);
        if (props.onVacationUpdate && vacation) {
            props.onVacationUpdate(vacation);
        }

        setIsUpdatingWeek(false);
        setUpdatingWeekNumber(null);
    }
    const getCalendarGrid = (): JSX.Element[] => {
        const daysBeforeFirst = getDaysBeforeFirst();
        const daysInMonth = getDaysOfMonth();
        const numberOfWeeks = getNumberOfWeeks();
        const paddedMonth = props.month.toString().padStart(2, '0');
        const firstOfMonth = moment(`${props.year}-${paddedMonth}-01`);
        const firstWeekNumber = firstOfMonth.isoWeek();

        const grid: JSX.Element[] = [];
        let dayCounter = 1 - daysBeforeFirst;

        for (let week = 0; week < numberOfWeeks; week++) {
            const currentWeekNumber = week + firstWeekNumber;
            const isThisWeekUpdating = isUpdatingWeek && updatingWeekNumber === currentWeekNumber;

            grid.push(
                <Grid size={1} key={`week-${week}`} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 8,
                    paddingBottom: '2px',
                    cursor: isUpdatingWeek ? 'wait' : weekIsInPast(currentWeekNumber, props.year) || !isAuthenticated ? 'default' : 'pointer',
                    opacity: isUpdatingWeek && !isThisWeekUpdating ? 0.5 : 1,
                    transition: 'all 0.3s ease',
                    backgroundColor: isThisWeekUpdating ? '#e3f2fd' : 'transparent',
                    borderRadius: 1,
                    position: 'relative',
                    '&:hover': {
                        backgroundColor: isUpdatingWeek || weekIsInPast(currentWeekNumber, props.year) ? undefined : '#f5f5f5'
                    },
                    ...(isThisWeekUpdating && {
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                            '0%': {
                                backgroundColor: '#e3f2fd',
                                transform: 'scale(1)'
                            },
                            '50%': {
                                backgroundColor: '#bbdefb',
                                transform: 'scale(1.05)'
                            },
                            '100%': {
                                backgroundColor: '#e3f2fd',
                                transform: 'scale(1)'
                            }
                        }
                    })
                }}
                    onClick={() => { handleWeekNumberClick(props.year, currentWeekNumber) }}>
                    {isThisWeekUpdating ? (
                        <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            border: '1px solid #1976d2',
                            borderTopColor: 'transparent',
                            animation: 'spin 1s linear infinite',
                            '@keyframes spin': {
                                '0%': { transform: 'rotate(0deg)' },
                                '100%': { transform: 'rotate(360deg)' }
                            }
                        }} />
                    ) : (
                        currentWeekNumber
                    )}
                </Grid>
            );

            for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
                if (dayCounter <= 0 || dayCounter > daysInMonth) {
                    grid.push(
                        <Grid size={1} key={`empty-${week}-${dayOfWeek}`}>
                            <Day day={-1} />
                        </Grid>
                    );
                } else {
                    grid.push(
                        <Grid size={1} key={`day-${dayCounter}`}>
                            <Day day={dayCounter} month={props.month} year={props.year} vacation={props.vacation} onVacationUpdate={props.onVacationUpdate} activityType={props.activityType} isWeekUpdating={isThisWeekUpdating} />
                        </Grid>
                    );
                }
                dayCounter++;
            }
        }

        return grid;
    }
    return (
        <div style={{ maxWidth: 250, minWidth: 250, minHeight: 255, margin: 2 }}>
            <CardHeader title={`${getMonthName(props.month)}`} sx={{ paddingBottom: 0, marginLeft: 1, color: '#6d4646' }} />
            <CardContent>
                <Grid container columns={8} spacing={0}>
                    {getCalendarGrid()}
                </Grid>
            </CardContent>
        </div>
    );
};
export default Month;
